import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Layout, Plus, Check } from 'lucide-react';
import { useDialogA11y } from '@/hooks/useDialogA11y';

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  initialName?: string;
  title: string;
  /** When true, primary action is save/update (not create). */
  isEditMode?: boolean;
}

export const AccountModal: React.FC<AccountModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialName = '', 
  title,
  isEditMode = false,
}) => {
  const { t } = useTranslation('common');
  const [name, setName] = useState(initialName);
  const dialogRootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setName(initialName);
  }, [initialName, isOpen]);

  useDialogA11y(dialogRootRef, { isOpen, onClose });

  if (!isOpen) return null;

  const handleSave = () => {
    if (name.trim()) {
      onSave(name.trim());
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity animate-in fade-in duration-300"
        onClick={onClose}
      />

      {/* Container */}
      <div
        ref={dialogRootRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-modal-title"
        tabIndex={-1}
        className="relative w-full max-w-md bg-card border border-border modal-surface shadow-modal overflow-hidden animate-in side-in-from-bottom-4 zoom-in-95 duration-300 focus:outline-none"
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="absolute top-6 right-6 p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-xl transition-all"
        >
          <X size={18} aria-hidden />
        </button>

        <div className="p-10 space-y-10">
          <div className="flex flex-col items-center text-center gap-6">
            <div className="w-20 h-20 rounded-xl bg-primary/10 flex items-center justify-center text-primary transition-all duration-500">
              <Layout size={36} strokeWidth={2.5} />
            </div>
            <div>
              <h2 id="account-modal-title" className="text-2xl font-black text-foreground tracking-tight">{title}</h2>
              <p className="text-sm text-muted-foreground mt-1 font-medium">{t('shared.modals.account.description')}</p>
            </div>
          </div>

          <div className="space-y-8">
            <div className="space-y-3">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground ml-1">{t('shared.modals.account.nameLabel')}</label>
              <input 
                autoFocus
                type="text" 
                placeholder={t('shared.modals.account.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                className="w-full bg-secondary border border-border rounded-2xl py-4 px-6 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/40 focus:ring-4 focus:ring-primary/5 transition-all font-bold"
              />
            </div>
          </div>

          <button 
            disabled={!name.trim()}
            onClick={handleSave}
            className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground py-4 rounded-2xl font-black active:scale-95 transition-all text-sm flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {isEditMode ? <Check size={18} /> : <Plus size={18} />}
            {isEditMode ? t('shared.modals.account.save') : t('shared.modals.account.create')}
          </button>
        </div>
      </div>
    </div>
  );
};
